import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardActionArea,
  Typography,
  CircularProgress,
} from "@mui/material";
import { getAllProducts } from "../../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../../services/indexMediaBaseUrl";

const NewArrivalsPage = () => {
  const { t, i18n } = useTranslation();
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  useEffect(() => {
		document.title = 'DIDA MAX — New Arrivals';
	}, []);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const MAX_ITEMS = 8;

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getAllProducts();
        if (res && res.success && Array.isArray(res.products)) {
          const flat = [];
          res.products.forEach((entry) => {
            if (Array.isArray(entry.products)) flat.push(...entry.products);
          });
          flat.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setProducts(flat.slice(0, MAX_ITEMS));
        } else {
          setProducts([]);
        }
      } catch (err) {
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getName = (p) => (lang === "ar" ? p.name_ar || p.name_en : p.name_en || p.name_ar);
  const getImage = (p) => getImageUrl(p.main_photo || (p.images && p.images[0] && p.images[0].image) || "");
  // helpers: pick price entry by stored country_id and format
  const getSelectedCountryId = () => {
    try {
      const v = localStorage.getItem("country_id");
      return v !== null ? String(v) : null;
    } catch {
      return null;
    }
  };

  const getPriceInfo = (product) => {
    if (!product) return null;
    const countryId = getSelectedCountryId();
    const curr = product.currencies || product.Currencies || [];
    let entry = null;
    if (countryId) entry = curr.find((c) => String(c.country_id) === String(countryId));
    if (!entry && curr.length) entry = curr[0];
    if (!entry) {
      const fallback = product.price || product.price_after_discount || null;
      return fallback
        ? { price: String(product.price || ""), price_after_discount: product.price_after_discount ? String(product.price_after_discount) : null }
        : null;
    }
    const rawPrice = entry.price != null ? String(entry.price) : null;
    const rawDiscount =
      entry.price_after_discount != null
        ? String(entry.price_after_discount)
        : null;

    const priceNum = parseFloat(rawPrice || "");
    const discountNum = parseFloat(rawDiscount || "");
    const hasValidDiscount =
      Number.isFinite(discountNum) &&
      discountNum > 0 &&
      (!Number.isFinite(priceNum) || discountNum < priceNum);

    return {
      price: rawPrice,
      price_after_discount: hasValidDiscount ? rawDiscount : null,
    };
  };

  const formatPrice = (p) => (p == null ? "" : p);

  if (loading) {
    return (
      <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography color="error">
          {t("error_loading_new_arrivals", { defaultValue: lang === "ar" ? "حدث خطأ في تحميل الوصل حديثاً" : "Error loading new arrivals" })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 5, px: 2 }}>
      <Container maxWidth="lg">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Typography variant="h4" fontWeight="bold">
            {t("new_arrivals_title", { defaultValue: lang === "ar" ? "وصل حديثاً" : "New arrivals" })}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {products.map((product) => (
            <Grid item xs={12} sm={6} md={3} key={product.id}>
              <Card sx={{ height: "100%", position: "relative", "&:hover": { boxShadow: 4 } }}>
                <CardActionArea component={Link} to={`/product/${product.id}`} sx={{ p: 2, textAlign: "center" }}>
                  <Box component="img" src={getImage(product)} alt={getName(product)} sx={{ width: 80, height: 80, objectFit: "cover", mb: 2, borderRadius: 1 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {getName(product)}
                  </Typography>
                  {/* price: عرض السعر وفق country_id المخزن */}
                  {(() => {
                    const pinfo = getPriceInfo(product);
                    if (!pinfo) return null;
                    const orig = formatPrice(pinfo.price);
                    const disc = formatPrice(pinfo.price_after_discount);
                    return disc ? (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                          {orig} ج.م
                        </Typography>
                        <Typography variant="h6" color="primary" fontWeight="bold">
                          {disc} ج.م
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="h6" color="primary" fontWeight="bold" sx={{ mt: 1 }}>
                        {orig} ج.م
                      </Typography>
                    );
                  })()}
                  {product.reviews_summary && (
                    <Typography variant="body2" color="text.secondary">
                      {product.reviews_summary.average_rating
                        ? `${product.reviews_summary.average_rating} ★ (${product.reviews_summary.total_reviews || 0})`
                        : t("no_rating", { defaultValue: lang === "ar" ? "بدون تقييم" : "No rating" })}
                    </Typography>
                  )}
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default NewArrivalsPage;
